import axios from "axios";
import { getToken } from "../services/AuthToken";
import { enqueueSnackbar } from "notistack";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
});

// Request interceptor → attach token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor → central error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;

    if (response) {
      const { status, data } = response;

      // 🔒 Unauthorized or JWT Expired
      if (
        status === 401 ||
        (status === 500 && data?.message?.toLowerCase().includes("jwt is expired"))
      ) {
        enqueueSnackbar("Session expired. Please log in again.", { variant: "error" });
        localStorage.removeItem("access_token");
        setTimeout(() => {
          window.location.href = "/login";
        }, 1500);
      }

      // 🚫 Forbidden
      else if (status === 403) {
        const resource = data?.resource ? ` ${data.resource}` : "";
        enqueueSnackbar(
          data?.message || `You do not have permission to access${resource} resource.`,
          { variant: "error" }
        );
      }

      // ❌ Not Found
      else if (status === 404) {
        enqueueSnackbar(data?.message || "Resource not found.", { variant: "warning" });
      }

      // 💥 Server Error
      else if (status >= 500) {
        enqueueSnackbar(
          data?.message || "Server error. Please try again later.",
          { variant: "error" }
        );
      }
    } else {
      // 🌐 Network Error
      enqueueSnackbar("Network error. Please check your connection.", { variant: "error" });
    }

    return Promise.reject(error);
  }
);

// Export API client wrapper
const apiClient = {
  get: (url, config = {}) => axiosInstance.get(url, config),
  post: (url, data, config = {}) => axiosInstance.post(url, data, config),
  put: (url, data, config = {}) => axiosInstance.put(url, data, config),
  delete: (url, config = {}) => axiosInstance.delete(url, config),
};

export default apiClient;
